import React, { useCallback, useEffect, useState } from "react";
import { Alert, Box, Chip, CircularProgress, MenuItem, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TextField, Typography } from "@mui/material";
import { alpha } from "@mui/material/styles";
import AccountBalanceWalletOutlinedIcon from "@mui/icons-material/AccountBalanceWalletOutlined";
import PaymentsOutlinedIcon from "@mui/icons-material/PaymentsOutlined";
import ReceiptLongOutlinedIcon from "@mui/icons-material/ReceiptLongOutlined";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import DashboardLayout from "../../../layouts/DashboardLayout";
import PageHeader from "../../../components/layout/PageHeader";
import KpiTile from "../components/KpiTile";
import { GraficoCartera, GraficoMensual, GraficoSucursal } from "../components/Graficos";
import { useColoresGrafico } from "../utils/colores";
import { MESES, bs, numero, variacion } from "../utils/formato";
import { getDashboardFacturacion } from "../services/dashboardFacturacionService";

const SUCURSALES = ["La Paz", "Cochabamba", "Santa Cruz"];
const PRIMER_ANIO = 2022;

function Tarjeta({ titulo, subtitulo, children }) {
  return (
    <Paper variant="outlined" sx={{ p: 1.75, minWidth: 0 }}>
      <Typography sx={{ fontSize: 14.5, fontWeight: 800 }}>{titulo}</Typography>
      {subtitulo && <Typography sx={{ fontSize: 12.5, color: "text.secondary", mb: 1 }}>{subtitulo}</Typography>}
      {children}
    </Paper>
  );
}

// Forma de cobro del mes: filas con su porcentaje y una barra (el riel es el mismo tono, más claro).
function FormasDeCobro({ formas }) {
  const c = useColoresGrafico();
  const total = formas.reduce((s, f) => s + f.monto, 0);
  if (!formas.length) return <Typography variant="body2" color="text.secondary">No hubo cobros en este mes.</Typography>;
  return (
    <Box sx={{ display: "grid", gap: 1.5, mt: 1 }}>
      {formas.map((f) => {
        const pct = total ? (f.monto / total) * 100 : 0;
        return (
          <Box key={f.forma}>
            <Box sx={{ display: "flex", alignItems: "baseline", gap: 1 }}>
              <Typography sx={{ fontSize: 13.5, fontWeight: 700, flex: 1 }}>{f.forma}</Typography>
              <Typography sx={{ fontSize: 13.5, fontWeight: 700 }}>{bs(f.monto)}</Typography>
              <Typography sx={{ fontSize: 12.5, color: "text.secondary", width: 48, textAlign: "right" }}>{pct.toLocaleString("es-BO", { maximumFractionDigits: 1 })}%</Typography>
            </Box>
            <Box sx={{ height: 8, borderRadius: 4, bgcolor: alpha(c.cobrado, 0.18), mt: 0.5, overflow: "hidden" }}>
              <Box sx={{ width: `${Math.max(pct, 1.5)}%`, height: "100%", borderRadius: 4, bgcolor: c.cobrado }} />
            </Box>
            <Typography sx={{ fontSize: 11.5, color: "text.secondary", mt: 0.25 }}>{numero(f.pagos)} pagos</Typography>
          </Box>
        );
      })}
    </Box>
  );
}

function TopDeudores({ filas }) {
  if (!filas.length) return <Typography variant="body2" color="text.secondary">No hay saldos pendientes.</Typography>;
  return (
    <TableContainer>
      <Table size="small" sx={{ "& .MuiTableCell-root": { py: 0.5, px: 1, fontSize: 13 } }}>
        <TableHead>
          <TableRow>
            <TableCell sx={{ fontWeight: 800 }}>Cliente</TableCell>
            <TableCell sx={{ fontWeight: 800 }}>Servicio</TableCell>
            <TableCell sx={{ fontWeight: 800 }}>Sucursal</TableCell>
            <TableCell sx={{ fontWeight: 800 }} align="right">Facturas</TableCell>
            <TableCell sx={{ fontWeight: 800 }} align="right">La más antigua</TableCell>
            <TableCell sx={{ fontWeight: 800 }} align="right">Por cobrar (Bs)</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {filas.map((d) => (
            <TableRow key={d.cod_servicio_cliente} hover>
              <TableCell>{d.cliente}</TableCell>
              <TableCell>
                {d.servicio || d.tipo_servicio || "-"}
                <Typography component="span" sx={{ fontSize: 12, color: "text.secondary", ml: 0.75 }}>
                  {d.tipo_servicio && d.servicio ? `${d.tipo_servicio} · ` : ""}#{d.cod_servicio_cliente}
                </Typography>
              </TableCell>
              <TableCell>{d.sucursal || "-"}</TableCell>
              <TableCell align="right" sx={{ fontVariantNumeric: "tabular-nums" }}>{numero(d.facturas)}</TableCell>
              <TableCell align="right">
                {d.dias > 90 ? (
                  <Chip size="small" icon={<WarningAmberIcon />} label={`${numero(d.dias)} días`} variant="outlined" sx={{ height: 22, fontSize: 12, fontWeight: 700 }} />
                ) : (
                  <Box component="span" sx={{ fontVariantNumeric: "tabular-nums" }}>{numero(d.dias)} días</Box>
                )}
              </TableCell>
              <TableCell align="right" sx={{ fontWeight: 700, fontVariantNumeric: "tabular-nums" }}>{numero(d.saldo)}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

// Dashboard de facturación y cobros. Todo en Bs. El mes elegido manda sobre los indicadores, las sucursales y la forma de cobro;
// el gráfico mensual muestra el año completo y la cartera es la de hoy.
export default function DashboardFacturacionPage() {
  const hoy = new Date();
  const [year, setYear] = useState(hoy.getFullYear());
  const [mes, setMes] = useState(hoy.getMonth() + 1);
  const [sucursal, setSucursal] = useState("");
  const [datos, setDatos] = useState(null);
  const [cargando, setCargando] = useState(true);
  const [error, setError] = useState("");

  const cargar = useCallback(async () => {
    setCargando(true);
    try {
      const { data } = await getDashboardFacturacion({ year, mes, sucursal: sucursal || undefined });
      setDatos(data);
      if (data.filtros.sucursal_fija) setSucursal(data.filtros.sucursal);
      setError("");
    } catch (e) {
      setError(e?.response?.data?.message || e?.response?.data?.error || "No se pudo cargar el dashboard.");
    } finally {
      setCargando(false);
    }
  }, [year, mes, sucursal]);

  useEffect(() => { cargar(); }, [cargar]);

  const anios = Array.from({ length: hoy.getFullYear() - PRIMER_ANIO + 1 }, (_, i) => hoy.getFullYear() - i);
  const sucursalFija = !!datos?.filtros?.sucursal_fija;
  const k = datos?.kpis;
  const mesAnterior = MESES[(mes + 10) % 12];
  const pctMas90 = k && k.cartera_total ? (k.cartera_mas_90 / k.cartera_total) * 100 : 0;
  const tituloMes = `${MESES[mes - 1]} ${year}`;

  return (
    <DashboardLayout>
      <PageHeader title="Dashboard de facturación" subtitle="Facturado, cobrado y cartera por cobrar · montos en bolivianos" />

      <Box sx={{ p: { xs: 1.5, md: 2 }, display: "grid", gap: 1.5 }}>
        <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", alignItems: "center" }}>
          <TextField size="small" select label="Año" value={year} onChange={(e) => setYear(Number(e.target.value))} sx={{ width: 110 }}>
            {anios.map((a) => <MenuItem key={a} value={a}>{a}</MenuItem>)}
          </TextField>
          <TextField size="small" select label="Mes" value={mes} onChange={(e) => setMes(Number(e.target.value))} sx={{ width: 150 }}>
            {MESES.map((m, i) => <MenuItem key={m} value={i + 1}>{m}</MenuItem>)}
          </TextField>
          <TextField size="small" select label="Sucursal" value={sucursal} onChange={(e) => setSucursal(e.target.value)} disabled={sucursalFija} sx={{ width: 170 }} slotProps={{ select: { displayEmpty: true }, inputLabel: { shrink: true } }}>
            <MenuItem value="">Todas</MenuItem>
            {SUCURSALES.map((s) => <MenuItem key={s} value={s}>{s}</MenuItem>)}
          </TextField>
          {cargando && <CircularProgress size={18} />}
        </Box>

        {error && <Alert severity="error" onClose={() => setError("")}>{error}</Alert>}

        {!datos && cargando && <Box sx={{ p: 6, display: "grid", placeItems: "center" }}><CircularProgress /></Box>}

        {datos && (
          <>
            <Box sx={{ display: "grid", gap: 1.5, gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr", lg: "repeat(4, 1fr)" } }}>
              <KpiTile etiqueta={`Facturado en ${MESES[mes - 1]}`} valor={bs(k.facturado)} icono={<ReceiptLongOutlinedIcon fontSize="small" />} variacion={variacion(k.facturado, k.facturado_anterior)} referencia={mesAnterior.toLowerCase()} />
              <KpiTile etiqueta={`Cobrado en ${MESES[mes - 1]}`} valor={bs(k.cobrado)} icono={<PaymentsOutlinedIcon fontSize="small" />} variacion={variacion(k.cobrado, k.cobrado_anterior)} referencia={mesAnterior.toLowerCase()} />
              <KpiTile etiqueta="Por cobrar hoy" valor={bs(k.cartera_total)} icono={<AccountBalanceWalletOutlinedIcon fontSize="small" />} detalle={`${numero(k.cartera_facturas)} facturas pendientes`} />
              <KpiTile etiqueta="Con más de 90 días" valor={bs(k.cartera_mas_90)} icono={<WarningAmberIcon fontSize="small" />} detalle={`${pctMas90.toLocaleString("es-BO", { maximumFractionDigits: 1 })}% de lo que falta cobrar`} />
            </Box>

            <Box sx={{ display: "grid", gap: 1.5, gridTemplateColumns: { xs: "1fr", lg: "minmax(0, 2fr) minmax(0, 1fr)" } }}>
              <Tarjeta titulo={`Facturado y cobrado por mes · ${year}`} subtitulo={`Mes elegido: ${MESES[mes - 1]}`}>
                <GraficoMensual mensual={datos.mensual} mes={mes} year={year} onElegirMes={(m) => m && setMes(m)} />
              </Tarjeta>
              <Tarjeta titulo="Por cobrar según antigüedad" subtitulo={`${bs(k.cartera_total)} en ${numero(k.cartera_facturas)} facturas`}>
                <GraficoCartera cartera={datos.cartera} />
              </Tarjeta>
            </Box>

            <Box sx={{ display: "grid", gap: 1.5, gridTemplateColumns: { xs: "1fr", lg: "repeat(2, minmax(0, 1fr))" } }}>
              <Tarjeta titulo={`Por sucursal · ${tituloMes}`} subtitulo="La facturación va a la sucursal que factura al cliente; el cobro, a la sucursal que lo registró.">
                <GraficoSucursal datos={datos.por_sucursal} tituloMes={tituloMes} />
              </Tarjeta>
              <Tarjeta titulo={`Cobros por forma de pago · ${tituloMes}`} subtitulo="Solo ingresos y franquicias; no cuentan intercambios ni castigos.">
                <FormasDeCobro formas={datos.forma_pago} />
              </Tarjeta>
            </Box>

            <Tarjeta titulo="Servicios con más saldo pendiente" subtitulo="Por servicio de cliente: facturas emitidas y no pagadas, a hoy. Con ⚠ las que pasan de 90 días.">
              <TopDeudores filas={datos.top_deudores} />
            </Tarjeta>
          </>
        )}
      </Box>
    </DashboardLayout>
  );
}
